import { forwardRef, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { POSITIONS } from '@hivebrite/design-system/Tooltip';

import BuilderThemeProvider from 'hb-builder/components/common/BuilderThemeProvider';
import { useComponentContext, useSectionContext } from 'hb-builder/hooks';

import ToolbarDelete from './Delete';
import ToolbarDrag from './Drag';
import ToolbarDuplicate from './Duplicate';
import ToolbarEdit from './Edit';

const ITEMS = {
  delete: ToolbarDelete,
  drag: ToolbarDrag,
  duplicate: ToolbarDuplicate,
  edit: ToolbarEdit,
};

const TOOLBAR_SETTINGS = Object.freeze({
  section: {
    tooltip: POSITIONS.LEFT,
    direction: 'column',
  },
  structure: {
    tooltip: POSITIONS.LEFT,
    direction: 'column',
  },
  single: {
    tooltip: POSITIONS.BOTTOM,
    direction: 'row',
  },
});

const Toolbar = forwardRef(({ items, isDragging, ...otherProps }, dragRef) => {
  const { isInLastColumn, type } = useComponentContext();
  const { bodyWidth } = useSectionContext();
  const [sectionToolbarTopPosition, setSectionToolbarTopPosition] =
    useState('50%');

  const toolbarRef = useRef();
  const fullHeightTrackerRef = useRef();

  useEffect(() => {
    const sectionHeight = fullHeightTrackerRef.current?.offsetHeight;

    setSectionToolbarTopPosition(
      sectionHeight ? `${sectionHeight / 2}px` : '50%',
    );
  }, []);

  return (
    <BuilderThemeProvider>
      {/* Need to create container taking the full height of the parent element
      to compute the toolbar top position in pixels when vertically centered.
      Otherwise the drag option doesn't work */}
      <FullHeightTracker ref={fullHeightTrackerRef} />
      <Wrapper
        ref={toolbarRef}
        type={type}
        $sectionToolbarTopPosition={sectionToolbarTopPosition}
        $isInLastColumn={isInLastColumn}
        $isPageFullWidth={bodyWidth === '100%'}
        {...otherProps}
      >
        {items
          .map((name) => {
            const ToolbarItem = ITEMS[name];

            return (
              ToolbarItem && (
                <Item
                  key={name}
                  bg={`builder.components.${type}`}
                  data-testid={`toolbar-${type}-${name}`}
                  $type={type}
                >
                  <ToolbarItem
                    isDragging={isDragging}
                    tooltipPlace={TOOLBAR_SETTINGS[type].tooltip}
                    ref={name === 'drag' ? dragRef : undefined}
                  />
                </Item>
              )
            );
          })
          .filter(Boolean)}
      </Wrapper>
    </BuilderThemeProvider>
  );
});

Toolbar.defaultProps = {
  items: [],
};

export default Toolbar;

const getToolbarPosition = ({
  type,
  $sectionToolbarTopPosition,
  $isInLastColumn,
  $isPageFullWidth,
}) => {
  const rightPositionWithSpaces =
    $isInLastColumn && $isPageFullWidth ? '24px' : 0;

  switch (type) {
    case 'section':
    case 'structure':
      return css`
        flex-direction: ${TOOLBAR_SETTINGS[type].direction};
        right: 0;
        top: ${$sectionToolbarTopPosition};
        transform: translate3d(0, -50%, 0);
        z-index: 500;

        > * {
          &:first-child {
            border-top-left-radius: 3px;
          }

          &:last-child {
            border-bottom-left-radius: 3px;
          }
        }
      `;

    case 'single':
    default:
      return css`
        flex-direction: ${TOOLBAR_SETTINGS[type].direction};
        left: auto;
        right: ${rightPositionWithSpaces};
        top: 0;
        z-index: 500;
        // Override the font-size of Box
        font-size: 0 !important;

        > * {
          &:first-child {
            border-top-left-radius: 3px;
          }

          &:last-child {
            border-top-right-radius: 3px;
          }
        }
      `;
  }
};

const newToolbarBoxStyles = () => css`
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 30px 0 rgba(0, 0, 0, 0.2);
`;

const FullHeightTracker = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
`;

const Wrapper = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['type'].includes(prop),
  })
  .attrs(() => ({
    alignItems: 'center',
    justifyContent: 'center',
  }))`
    line-height: 12px !important;
    pointer-events: none;
    position: absolute;

    ${getToolbarPosition}

    ${({ type, theme }) => css`
      ${type === 'section' && newToolbarBoxStyles()}
      margin: ${type !== 'section' ? '8px' : '0'};
      padding: 2px 4px;
      z-index: ${theme.zIndices.positioned};
    `}
  `;

const Item = styled(Box).attrs(() => ({
  alignItems: 'center',
  color: 'white',
  justifyContent: 'center',
  p: 1,
}))`
  cursor: pointer;
  pointer-events: all;

  ${({ $type }) => {
    const padding = $type !== 'section' ? '0 2px' : '2px 0';

    return `
          background-color: unset;
          padding: ${padding} !important;
        `;
  }}
`;
